import {
  queryOptions,
  useMutation,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { CheckCircle2, Mail, Users } from "lucide-react"
import { toast } from "sonner"

import { InvitesSkeleton } from "@/components/skeletons/invites-skeleton"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { acceptInvite, listPendingInvites } from "@/http/invites"
import { useOrganization } from "@/store/organization"

const invitesQueryOptions = queryOptions({
  queryKey: ["pending-invites"],
  queryFn: async () => {
    const response = await listPendingInvites()
    if (response.error) {
      throw new Error(response.error.message)
    }

    return response.data
  },
})

export const Route = createFileRoute("/_authenticated/invites")({
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(invitesQueryOptions)
    return {
      title: "Convites",
      path: "/invites",
    }
  },
  component: PendingInvitesPage,
  pendingComponent: InvitesSkeleton,
})

function PendingInvitesPage() {
  const navigate = useNavigate()
  const { loadOrganizations } = useOrganization()

  const { data: pendingInvites, refetch } =
    useSuspenseQuery(invitesQueryOptions)

  const { mutate: accept, isPending: isAccepting } = useMutation({
    mutationFn: async (inviteId: string) => {
      return acceptInvite(inviteId)
    },
    onSuccess: async (response) => {
      if (response.error) {
        toast.error(response.error.message || "Erro ao aceitar convite")
        return
      }

      await loadOrganizations()
      const { organizations } = useOrganization.getState()
      if (organizations.length === 1) {
        navigate({
          to: "/$orgSlug/dashboard",
          params: { orgSlug: organizations[0].slug },
        })
        return
      }

      if (organizations.length > 1) {
        navigate({ to: "/select-organization" })
        return
      }

      navigate({ to: "/home" })
    },
    onError: () => {
      toast.error("Erro ao aceitar convite")
    },
  })

  const invites = pendingInvites?.invites ?? []

  if (!invites.length) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center space-y-3">
          <Mail className="size-12 mx-auto text-muted-foreground" />
          <div>
            <p className="text-lg font-medium">Sem convites pendentes</p>
            <p className="text-muted-foreground text-sm">
              Nenhum convite foi encontrado para sua conta.
            </p>
          </div>
          <Button onClick={() => navigate({ to: "/onboarding" })}>
            Criar organização
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2">
          <Users className="h-6 w-6" />
          <h1 className="text-2xl font-bold md:text-3xl">Convites pendentes</h1>
        </div>
        <p className="text-muted-foreground mt-2">
          Aceite um convite para participar de uma organização.
        </p>
      </div>

      <div className="grid gap-4">
        {invites.map((invite) => {
          const roleLabel = invite.role === "admin" ? "Administrador" : "Membro"
          const authorName = invite.author?.name || "Equipe"
          return (
            <Card key={invite.id}>
              <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div className="space-y-1">
                  <CardTitle>{invite.organization.name}</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Convidado por {authorName}
                  </p>
                </div>
                <div className="text-sm font-medium">Cargo: {roleLabel}</div>
              </CardHeader>
              <CardContent className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="text-sm text-muted-foreground">
                  Enviado em{" "}
                  {new Intl.DateTimeFormat("pt-BR", {
                    dateStyle: "medium",
                    timeStyle: "short",
                  }).format(new Date(invite.createdAt))}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => accept(invite.id)}
                    disabled={isAccepting}
                  >
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    Aceitar convite
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => refetch()}
                    disabled={isAccepting}
                  >
                    Atualizar
                  </Button>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>
    </div>
  )
}
